import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import budgetSuppliersApi, { BudgetSupplier } from '../../api/budgetSuppliersApi'
import { RootState } from '../rootState'
import { IPagination } from '../../types/pagination'

export interface SuppliersState {
  suppliers: BudgetSupplier[]
  paginatedSuppliers: IPagination<BudgetSupplier[]>
  loading: boolean
  error: string | null
  currentPage: number
  searchQuery: string
  sortField: string
  sortDirection: 'asc' | 'desc'
}

const initialState: SuppliersState = {
  suppliers: [],
  paginatedSuppliers: {
    count: 0,
    links: {
      next: null,
      previous: null
    },
    page_size: 10,
    results: [],
    current_page: 1,
    total_pages: 1,
    last_page: 1,
    from: 0,
    to: 0
  },
  loading: false,
  error: null,
  currentPage: 1,
  searchQuery: '',
  sortField: 'name',
  sortDirection: 'asc'
}

// Async thunks
export const fetchSuppliers = createAsyncThunk(
  'suppliers/fetchSuppliers',
  async () => {
    const suppliers = await budgetSuppliersApi.getSuppliers()
    return suppliers
  }
)

export const createSupplier = createAsyncThunk(
  'suppliers/createSupplier',
  async (supplier: Partial<BudgetSupplier>) => {
    const newSupplier = await budgetSuppliersApi.createSupplier(supplier as BudgetSupplier)
    return newSupplier
  }
)

export const updateSupplier = createAsyncThunk(
  'suppliers/updateSupplier',
  async ({ id, data }: { id: number; data: Partial<BudgetSupplier> }) => {
    const updatedSupplier = await budgetSuppliersApi.updateSupplier(id, data)
    return updatedSupplier
  }
)

export const deleteSupplier = createAsyncThunk(
  'suppliers/deleteSupplier',
  async (id: number) => {
    await budgetSuppliersApi.deleteSupplier(id)
    return id
  }
)

export const fetchPaginatedSuppliers = createAsyncThunk(
  'suppliers/fetchPaginatedSuppliers',
  async (params: {
    page?: number
    page_size?: number
    search?: string
    order?: string
  }) => {
    const paginatedData = await budgetSuppliersApi.getPaginatedSuppliers(params)
    return paginatedData
  }
)

// Slice
const suppliersSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload
      state.currentPage = 1
    },
    setSortField: (state, action) => {
      state.sortField = action.payload
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch suppliers
      .addCase(fetchSuppliers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        state.loading = false
        state.suppliers = action.payload
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch suppliers'
      })
      // Create supplier
      .addCase(createSupplier.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createSupplier.fulfilled, (state, action) => {
        state.loading = false
        state.suppliers.push(action.payload)
      })
      .addCase(createSupplier.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create supplier'
      })
      // Update supplier
      .addCase(updateSupplier.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateSupplier.fulfilled, (state, action) => {
        state.loading = false
        const index = state.suppliers.findIndex(s => s.id === action.payload.id)
        if (index !== -1) {
          state.suppliers[index] = action.payload
        }
      })
      .addCase(updateSupplier.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update supplier'
      })
      // Delete supplier
      .addCase(deleteSupplier.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteSupplier.fulfilled, (state, action) => {
        state.loading = false
        state.suppliers = state.suppliers.filter(s => s.id !== action.payload)
      })
      .addCase(deleteSupplier.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete supplier'
      })
      // Fetch paginated suppliers
      .addCase(fetchPaginatedSuppliers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPaginatedSuppliers.fulfilled, (state, action) => {
        state.loading = false
        console.log('✅ API Response:', {
          count: action.payload.count,
          suppliers: action.payload.results.map(s => ({ id: s.id, name: s.name }))
        })
        state.paginatedSuppliers = action.payload
      })
      .addCase(fetchPaginatedSuppliers.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch suppliers'
      })
  }
})

export const { clearError, setCurrentPage, setSearchQuery, setSortField, setSortDirection } = suppliersSlice.actions

// Selectors
export const selectSuppliers = (state: RootState) => state.suppliers.suppliers
export const selectPaginatedSuppliers = (state: RootState) => state.suppliers.paginatedSuppliers
export const selectSuppliersLoading = (state: RootState) => state.suppliers.loading
export const selectSuppliersError = (state: RootState) => state.suppliers.error
export const selectCurrentPage = (state: RootState) => state.suppliers.currentPage
export const selectSearchQuery = (state: RootState) => state.suppliers.searchQuery
export const selectSortField = (state: RootState) => state.suppliers.sortField
export const selectSortDirection = (state: RootState) => state.suppliers.sortDirection

export default suppliersSlice.reducer